<template>
  <div
    class="skeleton-block"
    :class="[animationClass, bgColorClass, customClass]"
    :style="[inlineStyle, customStyle]"
  ></div>
</template>

<script setup lang="ts">
import type { CSSProperties } from "vue";
import { normalizeClass } from "vue";
const isTailwindClass = (val: string) =>
  /^bg-[\w-]+$/.test(val) && val !== "bg";

const tailwindColorCache = new Map<string, string>();

type AnimationDirection = "vertical" | "horizontal";

const {
  animationDirection = "vertical",
  bgColor = "#eff2f7",
  shimmerColor,
  width = "1rem",
  height = "1rem",
  customClass,
  customStyle,
} = defineProps<{
  animationDirection?: AnimationDirection;
  // can take both css color and tailwind color classes (e.g. bg-gray-200) — if the latter, the shimmer color will be derived from the resolved CSS color at runtime
  bgColor?: string;
  shimmerColor?: string;
  width?: string;
  height?: string;
  customClass?: string | string[] | Record<string, boolean>;
  customStyle?: CSSProperties | string;
}>();

const customClassList = computed(() =>
  normalizeClass(customClass).split(" ").filter(Boolean),
);
const hasWidthClass = computed(() =>
  customClassList.value.some((c) => c.startsWith("w-")),
);
const hasHeightClass = computed(() =>
  customClassList.value.some((c) => c.startsWith("h-")),
);

// SSR-safe init: Tailwind classes can't be resolved server-side, so they start
// as undefined and fall back to the auto-derived shimmer in the gradient.
const resolvedShimmerColor = ref<string | undefined>(
  shimmerColor && !isTailwindClass(shimmerColor) ? shimmerColor : undefined,
);

const bgColorClass = computed(() =>
  isTailwindClass(bgColor) ? bgColor : undefined,
);

const inlineStyle = computed(
  (): CSSProperties => ({
    // Omit width/height when Tailwind size classes are present in customClass —
    // inline styles would override them due to higher CSS specificity.
    ...(hasWidthClass.value ? {} : { width }),
    ...(hasHeightClass.value ? {} : { height }),
    backgroundColor: isTailwindClass(bgColor) ? undefined : bgColor,
  }),
);

const gradient = computed(() => {
  const defaultShimmer = isTailwindClass(bgColor)
    ? "rgba(255, 255, 255, 0.2)"
    : `color-mix(in srgb, ${bgColor} 50%, white)`;
  const shimmer = resolvedShimmerColor.value ?? defaultShimmer;
  const axis = animationDirection === "horizontal" ? "90deg" : "180deg";
  return `linear-gradient(${axis}, transparent 0%, ${shimmer} 50%, transparent 100%)`;
});

const animationClass = computed(() => `skeleton-block--${animationDirection}`);

// Tailwind classes must be resolved to a CSS color before use in linear-gradient().
// getComputedStyle on a temp element is the only reliable resolution without a lookup table.
// import.meta.client guards against SSR; watchEffect keeps the value in sync with prop changes.
if (import.meta.client) {
  watchEffect(() => {
    if (!shimmerColor || !isTailwindClass(shimmerColor)) {
      resolvedShimmerColor.value = shimmerColor;
      return;
    }
    const cached = tailwindColorCache.get(shimmerColor);
    if (cached) {
      resolvedShimmerColor.value = cached;
      return;
    }
    const el = document.createElement("span");
    el.className = shimmerColor;
    document.body.appendChild(el);
    const resolved = getComputedStyle(el).backgroundColor;
    document.body.removeChild(el);
    tailwindColorCache.set(shimmerColor, resolved);
    resolvedShimmerColor.value = resolved;
  });
}
</script>

<style scoped>
.skeleton-block {
  position: relative;
  overflow: hidden;
}

.skeleton-block::after {
  content: "";
  position: absolute;
  inset: 0;
  background: v-bind(gradient);
}

.skeleton-block--vertical::after {
  background-size: 100% 200%;
  animation: skeleton-shimmer-vertical 1.8s ease-in-out infinite;
}

.skeleton-block--horizontal::after {
  background-size: 200% 100%;
  animation: skeleton-shimmer-horizontal 1.6s ease-in-out infinite;
}

@keyframes skeleton-shimmer-vertical {
  0% {
    background-position: 0 200%;
  }
  100% {
    background-position: 0 -200%;
  }
}

@keyframes skeleton-shimmer-horizontal {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
